/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from "react";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { Button, Offcanvas } from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { proxyAPI } from "../proxyAPI";
import EmailIcon from "../../assets/icons/icon_email-01.svg2";
import MessageIcon from "../../assets/icons/messages-01.svg2";

const validateEmail = (email) =>
  String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

function DetectedContacts({ filters, onSaveContacts = () => null }) {
  const [show, setShow] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const perPage = 100;
  const [loading, setLoading] = useState(false);
  const pageProps = JSON.parse(
    document.querySelector("#app").dataset.page
  ).props;

  // detect valid emails and valid numbers in the search filters
  const detectedEmails =
    filters?.[0]?.values?.filter((v) => validateEmail(v)) ?? [];

  const detectedMobiles = (
    filters?.[0]?.values?.filter((v) =>
      isValidPhoneNumber(v, pageProps.defaultCountry.country_code)
    ) ?? []
  ).map((num) => {
    const pn = parsePhoneNumber(num, pageProps.defaultCountry.country_code);
    return pn.countryCallingCode + pn.nationalNumber;
  });

  // check if the contacts exists in the db
  const findContacts = async () => {
    setLoading(true);

    const bodyContent = {
      filters: [
        {
          values: [...detectedEmails, ...detectedMobiles],
          formats: ["primary"],
          order: 0,
          operator: "is",
        },
      ],
      paginate: {
        order: "desc",
        order_by: "uuid",
        page,
        perPage,
      },
      fields: {
        uuids: [],
        primary: true,
      },
    };

    const data = await proxyAPI({
      prefix: "contacts/v1",
      method: "POST",
      url: "/find",
      body: bodyContent,
    });
    setContacts(data.contacts);
    if (data.pagination) {
      setTotal(data.pagination.total);
    }
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  useEffect(() => {
    if (
      filters.length > 0 &&
      !filters.some(
        (filter) =>
          !["empty", "not-empty"].includes(filter.operator) &&
          filter.values.length === 0
      ) &&
      (!filters.some((filter) => filter.formats.length === 0) ||
        !filters.some((filter) => filter.fields.length === 0))
    ) {
      setLoading(true);
      const timeout = setTimeout(() => {
        setPage(0);
        findContacts();
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [filters]);

  // contacts that do not exists in db
  const detectedContacts = [
    ...(detectedEmails
      .filter(
        (email) =>
          !contacts.some((c) =>
            c.details.some((d) => d.values.some((v) => v.data === email))
          )
      )
      .map((v) => ({
        uuid: v,
        channel: "email",
        details: [
          {
            field: {
              uuid: v,
              label: "New email",
              isEmail: true,
            },
            values: [
              {
                data: v,
                order: 0,
                option: null,
              },
            ],
          },
        ],
      })) ?? []),
    ...(detectedMobiles
      .filter(
        (mobile) =>
          !contacts.some((c) =>
            c.details.some((d) => d.values.some((v) => v.data === mobile))
          )
      )
      .map((v) => ({
        uuid: v,
        channel: "sms",
        details: [
          {
            field: {
              uuid: v,
              label: "New mobile",
              isSms: true,
            },
            values: [
              {
                data: v,
                order: 0,
                option: null,
              },
            ],
          },
        ],
      })) ?? []),
  ];

  // save individual contact
  const handleSaveContact = async (newContact) => {
    setLoading(true);
    const addedContact = await proxyAPI({
      prefix: "contacts/v1",
      method: "POST",
      url: "/first-or-create",
      body: newContact,
    });
    if (addedContact.message) {
      toast.error(addedContact.message);
    } else {
      onSaveContacts([addedContact.uuid]);
      setContacts((prev) => [...prev, addedContact]);
      toast.success(`Saved contact successfully: ${newContact.item}`);
    }
    setLoading(false);
  };

  // save all
  const handleSaveAll = async () => {
    setLoading(true);

    const bodyContent = {
      items: [
        ...detectedMobiles.map((c) => ({
          channel: "sms",
          item: c,
        })),
        ...detectedEmails.map((c) => ({
          channel: "email",
          item: c,
        })),
      ],
    };

    const data = await proxyAPI({
      prefix: "contacts/v1",
      method: "POST",
      url: "/first-or-create-many",
      body: bodyContent,
    });
    if (data) {
      onSaveContacts(data.contacts.map((c) => c.uuid));
      setContacts((prev) => [...prev, ...data.contacts]);
      toast.success(`Saved ${data.contacts.length} contacts successfully`);
    } else {
      toast.error(data.message);
    }
    setLoading(false);
  };

  return (
    <>
      {!loading && detectedContacts.length > 0 && (
        <div className="mt-4 mb-4">
          {detectedContacts.length} new contacts detected.{" "}
          {detectedContacts.length > 0 && (
            <Button
              className="p-0 align-baseline"
              variant="link"
              onClick={() => setShow(true)}
            >
              Click to see/add new contacts.
            </Button>
          )}
        </div>
      )}
      <Offcanvas
        placement="end"
        show={show}
        onHide={() => setShow(false)}
        className="z-3000"
        backdropClassName="z-2999"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Add new contacts</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-3">
            {detectedContacts.length} new contacts detected.
          </div>
          <table className="table">
            <thead>
              <tr>
                <th scope="col" />
                <th scope="col" className="text-center">
                  Comms Address
                </th>
                <th scope="col" className="text-center">
                  Comms Channel
                </th>
              </tr>
            </thead>
            <tbody>
              {detectedContacts.map((c) => (
                <tr key={c.uuid}>
                  <td>
                    <button
                      type="button"
                      title="Save contact"
                      className="btn btn-outline-primary btn-sm border-0"
                      onClick={() =>
                        handleSaveContact({
                          channel: c.channel,
                          item: c.uuid,
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </td>
                  <td className="text-center">{c.details[0].values[0].data}</td>
                  <td className="text-center">
                    {c.channel === "sms" && (
                      <span title="SMS">
                        <MessageIcon height="1rem" />
                      </span>
                    )}
                    {c.channel === "email" && (
                      <span title="Email">
                        <EmailIcon height="1rem" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {detectedContacts.length > 0 && (
            <button
              className="btn btn-primary w-100 btn-sm mt-3"
              type="button"
              title="Save all contacts in the list"
              onClick={handleSaveAll}
            >
              Save all
            </button>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default DetectedContacts;
